import { useEffect, useRef } from 'react'
import { store } from '../store/store'

export const useAppSyncSubscription = (
  eventId: string,
  isOrganizer: boolean = false,
  participantEmail: string,
  onPollUpdate: (data: any) => void,
  onMatchmakerUpdate?: (data: any) => void
) => {
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    if (!isOrganizer && eventId) {
      
      const apiEndpoint = process.env.REACT_APP_APPSYNC_API_ENDPOINT
      if (!apiEndpoint) return

      const { accessToken } = store.getState().auth
      if (!accessToken) return

      const host = apiEndpoint.replace('https://', '').replace('/graphql', '')
      const authHeader = btoa(
        JSON.stringify({
          host,
          Authorization: `Bearer ${accessToken}`,
        })
      )
      const payload = btoa(JSON.stringify({}))

      const realtimeUrl = `${apiEndpoint
        .replace('https://', 'wss://')
        .replace(
          '.appsync-api.',
          '.appsync-realtime-api.'
        )}?header=${authHeader}&payload=${payload}`

      wsRef.current = new WebSocket(realtimeUrl, 'graphql-ws')

      wsRef.current.onopen = () => {
        console.log('WebSocket connected')
        wsRef.current?.send(
          JSON.stringify({
            type: 'connection_init',
          })
        )
      }

      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data)
        console.log('WebSocket message:', message)

        if (message.type === 'connection_ack') {
          // Send poll subscription
          wsRef.current?.send(
            JSON.stringify({
              id: '1',
              type: 'start',
              payload: {
                data: JSON.stringify({
                  query: `subscription OnUpdateGatherApiPoll($eventId: String!) {
              onUpdateGatherApiPoll(eventId: $eventId) {
                eventId
                uniqueLiveEventEntityId
                status
                question
                responses
                creationDateTimeStampSeconds
                pushedDateTimeStampSeconds
              }
            }`,
                  variables: { eventId },
                }),
                extensions: {
                  authorization: {
                    Authorization: `Bearer ${accessToken}`,
                    host: host,
                  },
                },
              },
            })
          )

          // Send matchmaker subscription only if callback exists
          if (onMatchmakerUpdate) {
            wsRef.current?.send(
              JSON.stringify({
                id: '2',
                type: 'start',
                payload: {
                  data: JSON.stringify({
                    query: `subscription OnCreateMatchmakerPair($eventId: String!, $participantEmail: String!) {
                onCreateMatchmakerPair(eventId: $eventId, subscriptionTriggerEmail: $participantEmail) {
                  uniqueLiveEventEntityId
                  eventId
                  participantOneEmail
                  participantOneName
                  participantOneTitle
                  participantTwoEmail
                  participantTwoName
                  participantTwoTitle
                  participantThreeEmail
                  participantThreeName
                  participantThreeTitle
                  subscriptionTriggerEmail
                  participantEmails
                }
              }`,
                    variables: { eventId, participantEmail },
                  }),
                  extensions: {
                    authorization: {
                      Authorization: `Bearer ${accessToken}`,
                      host: host,
                    },
                  },
                },
              })
            )
          }
        } else if (message.type === 'data') {
          if (
            message.id === '1' &&
            message.payload?.data?.onUpdateGatherApiPoll
          ) {
            console.log(`poll: `, message.payload.data.onUpdateGatherApiPoll)
            pollCallbackRef.current(message.payload.data.onUpdateGatherApiPoll)
          } else if (
            message.id === '2' &&
            message.payload?.data?.onCreateMatchmakerPair
          ) {
            console.log(`match: `, message.payload.data.onCreateMatchmakerPair)
            matchmakerCallbackRef.current?.(
              message.payload.data.onCreateMatchmakerPair
            )
          }
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isOrganizer, participantEmail])

  // Stable callback refs to avoid dependency issues
  const pollCallbackRef = useRef(onPollUpdate)
  pollCallbackRef.current = onPollUpdate

  const matchmakerCallbackRef = useRef(onMatchmakerUpdate)
  matchmakerCallbackRef.current = onMatchmakerUpdate
}
